import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import {
  SUPER_ADMIN_CMS_GROUP,
  SUPER_ADMIN_MASTER_DATA_GROUPS,
  SUPER_ADMIN_NAV_SECTIONS,
  SUPER_ADMIN_P2P_GROUP,
  SUPER_ADMIN_ROLE_MANAGEMENT_GROUP,
  SUPER_ADMIN_USER_MANAGEMENT_GROUP,
  SUPER_ADMIN_DISPUTE_MANAGEMENT_GROUP,
  SUPER_ADMIN_TICKET_MANAGEMENT_GROUP,
  SUPER_ADMIN_FEEDBACK_MANAGEMENT_GROUP,
  SUPER_ADMIN_LOGISTICS_GROUP,
  getSuperAdminNavIsActive,
} from "../../config/superAdminNavigation";
import "../../assets/styles/authenticated-layout.scss";

function SuperAdminSidebarLink({ item, pathname, search }) {
  const Icon = item.icon;
  const active = getSuperAdminNavIsActive(pathname, item, search);

  return (
    <NavLink
      to={item.to}
      end={item.end === true}
      className={() => `app-sidebar__link${active ? " app-sidebar__link--active" : ""}`}
    >
      {Icon ? <Icon className="app-sidebar__icon" aria-hidden /> : null}
      <span>{item.label}</span>
    </NavLink>
  );
}

function groupHasActiveChild(pathname, items = [], search = "") {
  return items.some((item) => getSuperAdminNavIsActive(pathname, item, search));
}

function SuperAdminCollapsibleGroup({ group, pathname, search }) {
  const [open, setOpen] = useState(() =>
    groupHasActiveChild(pathname, group.items, search),
  );
  const groupRef = useRef(group);
  groupRef.current = group;

  useEffect(() => {
    if (groupHasActiveChild(pathname, groupRef.current.items, search)) {
      setOpen(true);
    }
  }, [pathname, search]);

  const GroupIcon = group.icon;
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const hasActive = groupHasActiveChild(pathname, group.items, search);
  const panelId = `sa-sidebar-group-${group.id}`;

  return (
    <div className={`app-sidebar__collapsible app-sidebar__collapsible--${group.id}`}>
      <button
        type="button"
        id={`${panelId}-trigger`}
        className={`app-sidebar__collapsible-trigger${hasActive ? " app-sidebar__collapsible-trigger--has-active" : ""}`}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={toggle}
      >
        {GroupIcon ? <GroupIcon className="app-sidebar__icon" aria-hidden /> : null}
        <span className="app-sidebar__collapsible-heading">{group.heading}</span>
        <FaChevronDown
          className={`app-sidebar__collapsible-chevron${open ? " app-sidebar__collapsible-chevron--open" : ""}`}
          aria-hidden
        />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={`${panelId}-trigger`}
        aria-hidden={!open}
        className={`app-sidebar__collapsible-panel${open ? " app-sidebar__collapsible-panel--open" : ""}`}
      >
        <div className="app-sidebar__collapsible-panel-inner">
          {group.items.map((item) => (
            <SuperAdminSidebarLink
              key={item.id}
              item={item}
              pathname={pathname}
              search={search}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminSidebar() {
  const { pathname, search } = useLocation();

  return (
    <aside className="app-sidebar" aria-label="Super Admin navigation">
      <nav className="app-sidebar__inner">
        {SUPER_ADMIN_NAV_SECTIONS.map((section) => (
          <div key={section.id} className="app-sidebar__section">
            {section.title ? (
              <div className="app-sidebar__section-title" role="presentation">
                {section.title}
              </div>
            ) : null}
            {(section.items || []).map((item) => (
              <SuperAdminSidebarLink
                key={item.id}
                item={item}
                pathname={pathname}
                search={search}
              />
            ))}
            {section.id === "overview" ? (
              <div className="app-sidebar__collapsible-root">
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_ROLE_MANAGEMENT_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_USER_MANAGEMENT_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_DISPUTE_MANAGEMENT_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_TICKET_MANAGEMENT_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_FEEDBACK_MANAGEMENT_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_LOGISTICS_GROUP}
                  pathname={pathname}
                  search={search}
                />
                {SUPER_ADMIN_MASTER_DATA_GROUPS.map((group) => (
                  <SuperAdminCollapsibleGroup
                    key={group.id}
                    group={group}
                    pathname={pathname}
                    search={search}
                  />
                ))}
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_CMS_GROUP}
                  pathname={pathname}
                  search={search}
                />
                <SuperAdminCollapsibleGroup
                  group={SUPER_ADMIN_P2P_GROUP}
                  pathname={pathname}
                  search={search}
                />
              </div>
            ) : null}
          </div>
        ))}
      </nav>
    </aside>
  );
}
